import { fillPath, route } from "./routes.js";
import { z } from "zod";
//#region src/invitations.ts
/**
* `/v1/admin/invitations*` — the three invitation routes the admin app's users pages call: create,
* list and revoke.
*
* Producer: `AdminInvitationsController` and `InvitationService` in
* `cloud/dp/core-api/src/admin/`. Consumer: the admin app's `AdminClient`. Permission keys:
* `users:manage` on create and revoke, `users:read` on the list.
*
* `AdminInvitationsController` carries `@Controller()` with no prefix and declares each path in
* full, so its fourth route sits at `/v1/invitations/accept`. That route is public, carries the
* invitation token as its credential, and has no admin-app caller, so it is outside this contract.
*
* The shell and the controller agree on the response here: `InvitationRecord`'s nine keys are
* `invitationPublicColumns` exactly. The token column is absent by construction at the repository
* boundary — it is the sole bearer credential for the public accept route, and a `users:read`
* caller who could lift one could mint an account at whatever role the invitation carried.
*
* Direction rule (R-28): every response schema here is `z.object`, so a member core-api adds is
* stripped rather than refused and one new column cannot red every page that reads the route.
* Every request schema is `z.strictObject`, so a key the server would reject is refused before
* the request leaves the client. No shape in this file travels in both directions.
*/
/**
* The created invitation row, projected by `invitationPublicColumns`. Token-free by construction.
*
* `role` is the requested role slug, defaulted to `'member'` by the service when the body omits it,
* and validated referentially against the built-ins and `auth.org_role_definitions` — an unknown
* slug is a 400 rather than a silent default. `status` is `'pending'` on a fresh row.
*
* Owner: `cloud/dp/core-api/src/db/schema/auth/invitations.ts`.
*/
const invitationRecordSchema = z.object({
	id: z.uuid(),
	organizationId: z.uuid(),
	email: z.string(),
	role: z.string(),
	invitedBy: z.uuid().nullable(),
	status: z.string(),
	expiresAt: z.iso.datetime(),
	acceptedAt: z.iso.datetime().nullable(),
	createdAt: z.iso.datetime()
});
/**
* `POST /v1/admin/invitations` — invite an email address at a role. Status 201, 409 when a user
* or a pending invitation already holds the address. Requires `users:manage`.
*/
const CREATE_INVITATION_ROUTE = route("POST", "/v1/admin/invitations");
/**
* The body of `POST /v1/admin/invitations`.
*
* `email` is a bare bounded-free string on the producer: `createInvitationSchema` declares
* `z.string()` with no format and no bounds, and the controller adds a defensive guard that turns a
* missing or empty value into a 400 rather than a Postgres NOT NULL 500. This schema keeps the
* producer's shape and adds the `.min(1)` the guard enforces, so a client cannot send a body the
* server will reject for a reason the schema could have caught.
*
* `role` is optional; the service defaults it to `'member'`. Only an owner may invite at `owner`,
* and a delegated caller may only invite into a role its consented scopes already cover.
*
* Owner: `createInvitationSchema` in `admin-invitations.controller.ts`.
*/
const createInvitationRequestSchema = z.strictObject({
	email: z.string().min(1),
	role: z.string().optional()
});
/**
* The body of `POST /v1/admin/invitations`: the created row, plus `mailQueued`.
*
* `mailQueued` is `true` when core-api's mail outbox took the invitation mail. It is `false` when
* core-api committed the invitation and wrote no mail. The causes of `false`:
* - the data plane has no mail transport (`MAIL_TRANSPORT=none`);
* - the send switch is off (`MAIL_SEND_ENABLED=false`);
* - the mail payload refuses the invitee address, for example a value with two addresses;
* - the org-name read, the branding read or the outbox write failed.
*
* The status is 201 in each case, so the admin app must read this member to tell the admin that
* the invitee gets no mail (`2026-10-07-mail-backend`, decision D-9, ruling R-017). The cause can
* be the address the admin typed, so the admin app must not name the transport as the only cause.
*
* The member is optional. A core-api from before the mail outbox does not send it, and the parse
* then omits it. A consumer must treat an absent member as unknown, and only `false` as "no mail".
*
* Owner: `CreatedInvitation` in `cloud/dp/core-api/src/admin/invitation.service.ts`.
*/
const createInvitationResponseSchema = invitationRecordSchema.extend({ mailQueued: z.boolean().optional() });
/**
* `GET /v1/admin/invitations` — the organization's invitations, newest first, as a bare array.
* Status 200. No paging. Requires `users:read`.
*
* The optional `status` query filters on the column; absent, every row of every status is
* returned. The query is {@link listInvitationsRequestSchema}.
*/
const LIST_INVITATIONS_ROUTE = route("GET", "/v1/admin/invitations");
/**
* `DELETE /v1/admin/invitations/{id}` — revoke one invitation, so its token no longer accepts.
* Status 200 with `{ message: 'Invitation revoked' }`. Requires `users:manage`.
*
* Errors: 404 `Invitation not found` when the id names no invitation in the caller's
* organization; 400 for a path id that is not a uuid (`ParseUUIDPipe`). The service sets
* `status` to `'revoked'` whatever the row held before, an accepted row included, and writes an
* `invitation_revoked` audit row in the same transaction.
*/
const REVOKE_INVITATION_ROUTE = route("DELETE", "/v1/admin/invitations/{id}");
/** The invitation id grammar: the producer runs `ParseUUIDPipe` on `{id}` and answers 400 otherwise. */
const invitationIdPathParamSchema = z.uuid();
/**
* Fill `/v1/admin/invitations/{id}`, the path of {@link REVOKE_INVITATION_ROUTE}.
*
* @param invitationId the invitation's uuid, a row's `id` from {@link invitationRecordSchema}.
*   Throws a `ZodError` when it is not a uuid: the empty string, a slug, a traversal such as
*   `../accept`, a value with a space, or anything over 36 characters. No request is built for a
*   refused id.
*/
function adminInvitationPath(invitationId) {
	return fillPath(REVOKE_INVITATION_ROUTE.path, { id: invitationId }, { id: invitationIdPathParamSchema });
}
/**
* The query of `GET /v1/admin/invitations`.
*
* `status` is one of the three values the producer writes: `pending` on create, `accepted` on
* accept, `revoked` on revoke. The column is a bare `varchar`, and until #3331 the producer bound
* `@Query('status')` as a bare string, so a value outside the three reached the database and
* returned an empty list. The producer now binds this schema's `status` per key and answers 400;
* the enum here refuses it before send, the same tightening the MCP tool's `listInvitationsArgs`
* applies. `expired` is not a status: expiry is `expiresAt` in the past.
*
* Owner: `AdminInvitationsController.listInvitations` and `listInvitationsArgs` in
* `cloud/dp/core-api/src/mcp/org-admin-read-tools.ts`.
*/
const listInvitationsRequestSchema = z.strictObject({ status: z.enum([
	"pending",
	"accepted",
	"revoked"
]).optional() });
/**
* The body of `GET /v1/admin/invitations` — one token-free {@link invitationRecordSchema} row per
* invitation. The row's `status` stays `z.string()`: the column is open, and a value outside the
* three must not make the list unreadable.
*/
const listInvitationsResponseSchema = z.array(invitationRecordSchema);
/**
* The body of `DELETE /v1/admin/invitations/{id}` — status 200,
* `{ message: 'Invitation revoked' }`.
*/
const revokeInvitationResponseSchema = z.object({ message: z.string() });
/** Every route this file names, for `route-census.ts`. */
const invitationsRoutes = Object.freeze([
	CREATE_INVITATION_ROUTE,
	LIST_INVITATIONS_ROUTE,
	REVOKE_INVITATION_ROUTE
]);
//#endregion
export { CREATE_INVITATION_ROUTE, LIST_INVITATIONS_ROUTE, REVOKE_INVITATION_ROUTE, adminInvitationPath, createInvitationRequestSchema, createInvitationResponseSchema, invitationRecordSchema, invitationsRoutes, listInvitationsRequestSchema, listInvitationsResponseSchema, revokeInvitationResponseSchema };

//# sourceMappingURL=invitations.js.map